import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  useTheme
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  TrendingUp,
  AttachMoney,
  ShoppingCart,
  Assessment,
  DateRange,
  Refresh,
  Inventory
} from '@mui/icons-material';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { getProfitAnalyticsSummary, getProfitAnalyticsOrders, getProfitAnalyticsCharts } from '../../services/api';
import CustomPagination from '../../components/common/CustomPagination';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ProfitAnalytics = () => {
  const { t } = useTranslation(['profitAnalytics', 'common']);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [charts, setCharts] = useState(null);
  const [filterWarning, setFilterWarning] = useState('');
  const [filters, setFilters] = useState({
    start_date: format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    status: '',
    min_profit: '',
    max_profit: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page + 1, // Backend expects 1-based
        page_size: pagination.rowsPerPage
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const [summaryData, ordersData, chartsData] = await Promise.all([
        getProfitAnalyticsSummary(params),
        getProfitAnalyticsOrders(params),
        getProfitAnalyticsCharts(params)
      ]);

      setSummary(summaryData);
      // DRF pagination returns {results: [...], count: ..., next: ..., previous: ...}
      const ordersList = Array.isArray(ordersData.results) ? ordersData.results : [];
      setOrders(ordersList);
      setCharts(chartsData);
      const outOfRangeOrders = ordersList.filter(order => {
        if (!order.created_at) return true;
        const createdDate = format(new Date(order.created_at), 'yyyy-MM-dd');
        return (
          (filters.start_date && createdDate < filters.start_date) ||
          (filters.end_date && createdDate > filters.end_date)
        );
      });
      setFilterWarning(outOfRangeOrders.length
        ? t('messages.outOfRangeOrders', { count: outOfRangeOrders.length })
        : ''
      );
      setPagination(prev => ({
        ...prev,
        totalCount: ordersData.count || ordersList.length || 0
      }));
    } catch (err) {
      setError(err.response?.data?.message || t('messages.error'));
      console.error('Error fetching profit analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(pagination.rowsPerPage.toString());
    } else {
      setPagination(prev => ({ ...prev, rowsPerPage: Number(value), page: 0 }));
      setCustomRowsActive(false);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setPagination(prev => ({ ...prev, rowsPerPage: val, page: 0 }));
    }
    setCustomRowsActive(false);
  };

  const formatCurrency = (amount, currency = 'RMB') => {
    if (amount === undefined || amount === null) return '0.00';
    if (currency === 'RMB') {
      return `¥${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      return `₦${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      3: t('status.purchased'),
      4: t('status.inWarehouse'),
      5: t('status.shippedOut'),
      6: t('status.inNigeria'),
      7: t('status.received'),
      9: t('status.success')
    };
    return statusMap[status] || 'Unknown';
  };

  const getStatusColor = (status) => {
    const colorMap = {
      3: '#2196F3',
      4: '#2196F3',
      5: '#9C27B0',
      6: '#FF5722',
      7: '#4CAF50',
      9: '#4CAF50'
    };
    return colorMap[status] || '#9E9E9E';
  };

  // Chart data preparation
  const profitTrendData = charts?.profit_trend ? {
    labels: charts.profit_trend.map(item => format(new Date(item.date), 'MMM dd')),
    datasets: [
      {
        label: 'Profit (RMB)',
        data: charts.profit_trend.map(item => item.total_profit_rmb),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  } : null;

  const profitByStatusData = charts?.profit_by_status ? {
    labels: charts.profit_by_status.map(item => item.status_label),
    datasets: [
      {
        label: 'Profit (RMB)',
        data: charts.profit_by_status.map(item => item.total_profit_rmb),
        backgroundColor: [
          '#2196F3',
          '#2196F3',
          '#9C27B0',
          '#FF5722',
          '#4CAF50',
          '#4CAF50'
        ]
      }
    ]
  } : null;

  // DataGrid columns
  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: t('table.sn'),
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'order_number',
      headerName: t('table.orderNumber'),
      flex: 1,
      minWidth: 120,
      sortable: false
    },
    {
      field: 'created_at',
      headerName: t('table.date'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      field: 'user',
      headerName: t('table.customer'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => params.row.user?.username || 'N/A'
    },
    {
      field: 'rate',
      headerName: t('table.orderRate'),
      width: 120,
      sortable: false,
      renderCell: (params) => params.value || 'N/A'
    },
    {
      field: 'actual_rate_used',
      headerName: t('table.actualRate'),
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === '') return '0.00';
        return parseFloat(value).toFixed(2);
      }
    },
    {
      field: 'total_price',
      headerName: t('table.totalRmb'),
      width: 140,
      sortable: false,
      renderCell: (params) => formatCurrency(params.value)
    },
    {
      field: 'actual_purchased_price',
      headerName: t('table.purchasedRmb'),
      width: 150,
      sortable: false,
      renderCell: (params) => formatCurrency(params.value)
    },
    {
      field: 'profit_on_exchange_rmb',
      headerName: t('table.profitExchangeRmb'),
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Typography sx={{ color: '#10b981', fontWeight: 'bold' }}>
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    {
      field: 'profit_on_order_rmb',
      headerName: t('table.profitOrderRmb'),
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Typography sx={{ color: '#10b981', fontWeight: 'bold' }}>
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    {
      field: 'total_profit_rmb',
      headerName: t('table.totalProfitRmb'),
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Typography sx={{ color: '#10b981', fontWeight: 'bold' }}>
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: t('table.status'),
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={getStatusLabel(params.value)}
          size="small"
          sx={{
            backgroundColor: getStatusColor(params.value),
            color: 'white',
            fontWeight: 'bold'
          }}
        />
      )
    }
  ], [t]);

  // Add serial numbers to orders
  const rowsWithSerial = useMemo(() => {
    return orders.map((order, idx) => ({
      ...order,
      id: order.id || idx,
      sn: (pagination.page * pagination.rowsPerPage) + idx + 1
    }));
  }, [orders, pagination.page, pagination.rowsPerPage]);

  if (loading && !summary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          💰 {t('title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('common:buttons.refresh')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {filterWarning && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setFilterWarning('')}>
          {filterWarning}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('filters.startDate')}
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('filters.endDate')}
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('filters.status')}</InputLabel>
                <Select
                  value={filters.status}
                  label={t('filters.status')}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">{t('filters.all')}</MenuItem>
                  <MenuItem value="3">{t('status.purchased')}</MenuItem>
                  <MenuItem value="4">{t('status.inWarehouse')}</MenuItem>
                  <MenuItem value="5">{t('status.shippedOut')}</MenuItem>
                  <MenuItem value="6">{t('status.inNigeria')}</MenuItem>
                  <MenuItem value="7">{t('status.received')}</MenuItem>
                  <MenuItem value="9">{t('status.success')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                label={t('filters.minProfit')}
                type="number"
                value={filters.min_profit}
                onChange={(e) => handleFilterChange('min_profit', e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                label={t('filters.maxProfit')}
                type="number"
                value={filters.max_profit}
                onChange={(e) => handleFilterChange('max_profit', e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ backgroundColor: '#f0f9ff', border: '2px solid #0ea5e9' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('summary.totalProfitRmb')}
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#0ea5e9', fontWeight: 'bold' }}>
                      {formatCurrency(summary.total_profit_rmb)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(summary.total_profit_naira, 'NGN')}
                    </Typography>
                  </Box>
                  <AttachMoney sx={{ fontSize: 40, color: '#0ea5e9' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ backgroundColor: '#f8fafc', border: '2px solid #64748b' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('summary.filteredOrders')}
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#334155', fontWeight: 'bold' }}>
                      {Number(summary.total_orders || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {filters.start_date} - {filters.end_date}
                    </Typography>
                  </Box>
                  <DateRange sx={{ fontSize: 40, color: '#64748b' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ backgroundColor: '#f0fdf4', border: '2px solid #10b981' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('summary.profitOnExchange')}
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#10b981', fontWeight: 'bold' }}>
                      {formatCurrency(summary.profit_on_exchange_rmb)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(summary.profit_on_exchange_naira, 'NGN')}
                    </Typography>
                  </Box>
                  <TrendingUp sx={{ fontSize: 40, color: '#10b981' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ backgroundColor: '#fef3c7', border: '2px solid #f59e0b' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('summary.profitOnOrders')}
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#f59e0b', fontWeight: 'bold' }}>
                      {formatCurrency(summary.profit_on_orders_rmb)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(summary.profit_on_orders_naira, 'NGN')}
                    </Typography>
                  </Box>
                  <ShoppingCart sx={{ fontSize: 40, color: '#f59e0b' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ backgroundColor: '#f3e8ff', border: '2px solid #8b5cf6' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('summary.avgProfitPerOrder')}
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#8b5cf6', fontWeight: 'bold' }}>
                      {formatCurrency(summary.average_profit_per_order_rmb)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                    {/* {summary.profitable_orders} / {summary.total_orders} {t('summary.profitableOrders')} */}
                      {formatCurrency(summary.average_profit_per_order_naira, 'NGN')}
                    </Typography>
                  </Box>
                  <Assessment sx={{ fontSize: 40, color: '#8b5cf6' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ backgroundColor: '#fff5f5', border: '2px solid #ef4444' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('summary.tradeVolume')}
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#ef4444', fontWeight: 'bold' }}>
                      {formatCurrency(summary.total_trade_volume)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(summary.total_trade_volume_naira, 'NGN')}
                    </Typography>
                  </Box>
                  <Inventory sx={{ fontSize: 40, color: '#ef4444' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ backgroundColor: '#f0fdfa', border: '2px solid #14b8a6' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('summary.avgAmountPerOrder')}
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#14b8a6', fontWeight: 'bold' }}>
                      {formatCurrency(summary.average_amount_per_order_rmb)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(summary.average_amount_per_order_naira, 'NGN')}
                    </Typography>
                  </Box>
                  <ShoppingCart sx={{ fontSize: 40, color: '#14b8a6' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Charts */}
      {charts && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('charts.profitTrend')}
                </Typography>
                {profitTrendData && (
                  <Box sx={{ height: 300 }}>
                    <Line data={profitTrendData} options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: true },
                        tooltip: { mode: 'index', intersect: false }
                      },
                      scales: {
                        y: { beginAtZero: true }
                      }
                    }} />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('charts.profitByStatus')}
                </Typography>
                {profitByStatusData && (
                  <Box sx={{ height: 300 }}>
                    <Bar data={profitByStatusData} options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false }
                      },
                      scales: {
                        y: { beginAtZero: true }
                      }
                    }} />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Exchange Rate Comparison */}
          {charts.rate_comparison && (
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('charts.exchangeRateComparison')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                    <Typography variant="body1">
                      <strong>{t('charts.actualRate')}:</strong> {charts.rate_comparison.actual_rate || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('charts.mostCommonRates')}:
                    </Typography>
                    {charts.rate_comparison.order_rates?.slice(0, 5).map((rate, idx) => (
                      <Chip
                        key={idx}
                        label={`${rate.rate} (${rate.order_count} orders)`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Orders Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Pagination */}
      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>
    </Box>
  );
};

export default ProfitAnalytics;
